import apiInvitado from "../api/Invitado/api-invitado";
import { exportState } from "../utils/exportState";
import { cerrarSesionYAvisar } from "./SesionRechazada";
import {
  confirmarVigencia,
  expiracionDelToken,
  tokenVencido,
  porVencer,
  MARGEN_DE_CONFIRMACION_MS,
} from "./vigenciaDelToken";

export { expiracionDelToken, tokenVencido, porVencer };

/**
 * Token de invitado que se está pidiendo en este momento. Al abrir un formulario sin sesión salen
 * varias peticiones a la vez —países, departamentos, comunas— y todas esperan este mismo token en
 * vez de pedir uno cada una. Se suelta al terminar, salga bien o mal, para que un fallo se reintente.
 */
let tokenDeInvitadoEnCurso: Promise<string> | null = null;

const pedirTokenDeInvitado = (): Promise<string> => {
  if (!tokenDeInvitadoEnCurso) {
    tokenDeInvitadoEnCurso = apiInvitado
      .tokenInvitado()
      .then((respuesta: any) => {
        const token = respuesta?.token;
        // Sin esta comprobación se guardaba el texto "undefined" como si fuera un token
        if (!token) throw new Error("El servicio de usuarios no entregó el token de invitado");

        /*
          No se pisa el token de quien entró mientras tanto.

          Al navegar sin sesión salen varias peticiones que piden este token. Si
          alguien inicia sesión mientras una está en vuelo, su respuesta llegaba
          después del login y dejaba guardado el de invitado en lugar del suyo.
          A partir de ahí, cada sección que exige sesión respondía 400 —"token
          invitado invalido (para esta sección)"— y ese mensaje se lee como
          sesión rechazada, así que lo sacaba.
        */
        if (!localStorage.getItem("user")) {
          localStorage.setItem("token", token);
        }

        return token;
      })
      .finally(() => {
        tokenDeInvitadoEnCurso = null;
      });
  }
  return tokenDeInvitadoEnCurso;
};

/**
 * Pide un token de invitado y lo deja listo para la petición en curso.
 *
 * Si mientras se pedía apareció una sesión, la petición se manda con el token
 * de esa sesión: ir con el de invitado es justo lo que el backend rechaza.
 */
const usarTokenDeInvitado = async (config: any): Promise<void> => {
  const deInvitado = await pedirTokenDeInvitado();
  const guardado = localStorage.getItem("token");
  const hayUsuario = !!localStorage.getItem("user");

  config.headers.Authorization = `${hayUsuario && guardado ? guardado : deInvitado}`;
};

/**
 * Se llama únicamente cuando consta que el token no sirve. Si hay un usuario en sesión, se le avisa
 * y se le manda al login; si era un invitado, basta con pedir otro token.
 */
const cerrarSesion = async (
  config: any,
  user: string | null,
  motivo = "sin motivo registrado"
): Promise<void> => {
  if (!user) {
    await usarTokenDeInvitado(config);
    return;
  }

  cerrarSesionYAvisar(
    "Sesión expirada",
    "Su sesión ha expirado, por favor inicie sesión nuevamente",
    motivo
  );
  throw new Error("Sesión expirada");
};

const ValidateTokenExistence = async (config: any, token: string) => {
  const user = localStorage.getItem("user");

  if (!token) {
    await usarTokenDeInvitado(config);
    return;
  }

  // Durante exportaciones masivas se adjunta el token sin más comprobaciones.
  if (exportState.suppressRedirect) {
    config.headers.Authorization = token;
    return;
  }

  // El propio token dice que ya venció: no hay nada que consultar.
  if (tokenVencido(token)) {
    await cerrarSesion(config, user, "el token traía dentro una fecha ya vencida");
    return;
  }

  // Vigente y con margen de sobra: se usa tal cual, sin preguntarle a nadie.
  if (!porVencer(token)) {
    config.headers.Authorization = token;
    return;
  }

  // Está por vencer o no se pudo leer: se confirma con el backend.
  try {
    // Compartida con la comprobacion de ruta: una pregunta por rafaga.
    const respuesta = await confirmarVigencia(token);

    if (respuesta?.status === 200) {
      config.headers.Authorization = token;
      return;
    }

    // Solo se cierra la sesión cuando el backend dice que el token NO sirve.
    // Cualquier otra respuesta —503 cuando no pudo comprobarlo, o una forma
    // que no se reconoce— se trata como "no se sabe": antes todo lo que no
    // fuera 200 sacaba al usuario, de modo que un tropiezo de la base de
    // datos del servicio de usuarios cerraba sesiones con el token intacto.
    if (respuesta?.status === 400) {
      await cerrarSesion(
        config,
        user,
        `users/token respondió 400 al confirmar el token: ${JSON.stringify(respuesta?.message ?? "")}`
      );
      return;
    }

    console.warn(
      "No se pudo confirmar la vigencia del token; se continúa con el actual.",
      respuesta
    );
    config.headers.Authorization = token;
  } catch (error: any) {
    if (error?.message === "Sesión expirada") throw error;

    // No se pudo confirmar. No es lo mismo que un token inválido: se sigue con el que hay y, si de
    // verdad está muerto, el endpoint real responderá 401.
    console.warn("No se pudo confirmar la vigencia del token; se continúa con el actual.", error);
    config.headers.Authorization = token;
  }
};

export default ValidateTokenExistence;

/**
 * ¿Es el token de invitado y no el de una sesión?
 *
 * El backend se lo emite a quien no ha entrado, con user_id -1, y lo guarda
 * en el mismo sitio que el de verdad. Despues de cerrar sesion, cualquier
 * peticion pendiente pide uno y lo escribe, de modo que en localStorage queda
 * un token sin datos de usuario al lado. Verlo y darlo por una sesion
 * caducada sacaba a la persona con el aviso de siempre, una y otra vez.
 */
export const esTokenDeInvitado = (token: string): boolean => {
  try {
    const carga = token.split(".")[1];
    if (!carga) return false;

    const { user_id } = JSON.parse(atob(carga.replace(/-/g, "+").replace(/_/g, "/")));
    return user_id === -1;
  } catch {
    return false;
  }
};
